import { groupBy } from "lodash-es";
import { all, call, put, select, take, takeLatest } from "redux-saga/effects";

import { EntityType } from "@mapmycustomers/shared/enum";
import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import type LeaderboardItem from "@mapmycustomers/shared/types/entity/LeaderboardItem";
import {
  AnyEntity,
  EntityTypesSupportingCustomMetrics,
} from "@mapmycustomers/shared/types/entity/types";
import Organization from "@mapmycustomers/shared/types/Organization";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import localSettings from "@app/config/LocalSettings";
import StackRankDrillDownViewState from "@app/scene/dashboard/store/stackRank/DrillDownViewState";
import StackRankViewState from "@app/scene/dashboard/store/stackRank/StackRankViewState";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import PerformanceData from "@app/scene/dashboard/types/PerformanceData";
import getRankingMetricEntityType from "@app/scene/dashboard/utils/getRankingMetricEntityType";
import upsertPerformanceData from "@app/scene/dashboard/utils/upsertPerformanceData";
import { isStackRankAvailable } from "@app/scene/reports/store/selectors";
import { ApiMethodName } from "@app/store/api/ApiService";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import { isOwner } from "@app/store/iam/util";
import { getTeams, getUsers } from "@app/store/members";
import { fetchCustomMetrics } from "@app/store/stackRank/actions";
import { DEFAULT_PAGE_SIZE } from "@app/util/consts";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import { CompanyFieldName } from "@app/util/fieldModel/CompanyFieldModel";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { isCustomField } from "@app/util/fieldModel/impl/assert";
import leaderboardFieldModel from "@app/util/fieldModel/LeaderboardFieldModel";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";
import { RouteFieldName } from "@app/util/fieldModel/RouteFieldModel";
import {
  createEmptyFilterModel,
  createSimpleCondition,
  upsertSimpleConditions,
} from "@app/util/filters/utils";
import loggingService from "@app/util/logging";
import { not } from "@app/util/logic";
import downloadStackRankDataAsCsv from "@app/util/stackRank/downloadStackRankDataAsCsv";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import dashboardSettings from "../../utils/dashboardSettings";
import getDefaultDrillDownColumns from "../utils/getDefaultDrillDownColumns";

import {
  applyUserDrillDownListViewSettings,
  exportDrillDownData,
  fetchDrillDownData,
  fetchMetrics,
  fetchUserDrillDownData,
  initializeStackRank,
  showUserDrillDown,
  updateDrillDownViewState,
  updateStackRankViewState,
} from "./actions";
import {
  getDrillDownData,
  getDrillDownViewState,
  getStackRankViewState,
  getUserDrillDownViewState,
} from "./selectors";

function* onInitialize() {
  try {
    // first - load custom metrics
    yield put(fetchCustomMetrics.request());
    yield take([fetchCustomMetrics.success, fetchCustomMetrics.failure]);

    // next - read view state
    const localSettingsViewState: StackRankViewState | undefined = yield call(
      dashboardSettings.getStackRankViewState
    );

    const drillDownViewState: StackRankDrillDownViewState = yield call(
      dashboardSettings.getStackRankDrillDownViewState
    );

    yield put(
      initializeStackRank.success({ drillDownViewState, viewState: localSettingsViewState })
    );
    yield put(fetchMetrics.request({}));
  } catch (error) {
    yield handleError({ error });
    yield put(initializeStackRank.failure());
  }
}

const getLocalUserRankMap = (
  globalRankMap: Map<User["id"], LeaderboardItem["rank"]>,
  scope: User["id"][]
): Record<User["id"], LeaderboardItem["rank"]> => {
  const userIdsRankGroups = groupBy(
    scope,
    (id) => globalRankMap.get(id) ?? Number.MAX_SAFE_INTEGER
  );

  let currentRank = 1;
  const userIdToRankMap: Record<User["id"], LeaderboardItem["rank"]> = {};
  Object.keys(userIdsRankGroups)
    .map((rank) => parseInt(rank, 10))
    .sort((a, b) => a - b)
    .forEach((rank) => {
      userIdsRankGroups[rank].forEach((userId) => {
        userIdToRankMap[userId] = currentRank;
      });
      currentRank++;
    });

  return userIdToRankMap;
};

const getLocalTeamRankInformation = (
  leaderboardItems: LeaderboardItem[],
  teams: Team[]
): Map<Team["id"], Map<User["id"], Pick<LeaderboardItem, "rank" | "rankDiff">>> => {
  const valuableLeaderboardItems = leaderboardItems.filter(
    ({ rank, user }) => user && isDefined(rank)
  );

  const currentUserRanks = new Map<User["id"], LeaderboardItem["rank"]>(
    valuableLeaderboardItems.map(({ rank, user }) => [user!.id, rank ?? 0])
  );
  const previousUserRanks = new Map<User["id"], LeaderboardItem["rank"]>(
    valuableLeaderboardItems.map(({ rank, rankDiff, user }) => [
      user!.id,
      (rank ?? 0) + (rankDiff ?? 0),
    ])
  );

  return new Map<Team["id"], Map<User["id"], Pick<LeaderboardItem, "rank" | "rankDiff">>>(
    teams.map((team) => {
      const teamUserIds = team.users.map(({ id }) => id);

      const currentRank = getLocalUserRankMap(currentUserRanks, teamUserIds);
      const previousRank = getLocalUserRankMap(previousUserRanks, teamUserIds);

      return [
        team.id,
        new Map(
          teamUserIds.map((userId) => [
            userId,
            {
              rank: currentRank[userId],
              rankDiff: (previousRank[userId] ?? currentRank[userId]!) - currentRank[userId]!,
            },
          ])
        ),
      ];
    })
  );
};

function* onFetchMetrics() {
  try {
    const stackRankVisibility: boolean = yield select(isStackRankAvailable);
    if (!stackRankVisibility) {
      yield put(fetchMetrics.success({}));
      return;
    }

    const stackRankViewState: StackRankViewState = yield select(getStackRankViewState);
    yield call(dashboardSettings.setStackRankViewState, stackRankViewState);
    const { dateRange, filters, rankingMetric } = stackRankViewState;

    const metricName = isCustomField(rankingMetric)
      ? rankingMetric.customFieldData.displayName
      : rankingMetric.name;
    const entityType = getRankingMetricEntityType(rankingMetric);
    const fieldModel = getFieldModelByEntityType(entityType);

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const teams: Team[] = yield select(getTeams);
    const users: User[] = yield select(getUsers);

    const teamsWithUsers = teams.filter(({ users }) => users.length);
    const usersWithoutTeam = users.filter(({ teams }) => !teams.length);
    const owners = usersWithoutTeam.filter(isOwner);
    const noTeamUsers = usersWithoutTeam.filter(not(isOwner));

    const payload = {
      ...convertToPlatformFilterModel(filters, [], fieldModel),
      date: {
        $gte: dateRange.startDate.toISOString(),
        $lte: dateRange.endDate.toISOString(),
      },
      includePerformanceMetric: true,
      property: { $in: [metricName] },
      summary: "user" as const,
    };

    const responses: ListResponse<LeaderboardItem>[] = yield all([
      callApi("fetchLeaderboardData", orgId, { ...payload, summary: "all" }, metricName),
      callApi("fetchLeaderboardData", orgId, { ...payload, summary: "team" }, metricName),
      ...(teamsWithUsers.length
        ? [
            callApi(
              "fetchLeaderboardData",
              orgId,
              {
                ...payload,
                userId: { $in: teamsWithUsers.flatMap((team) => team.users.map(({ id }) => id)) },
              },
              metricName
            ),
          ]
        : []),
      ...(owners.length
        ? [
            callApi(
              "fetchLeaderboardData",
              orgId,
              { ...payload, userId: { $in: owners.map(({ id }) => id) } },
              metricName
            ),
          ]
        : []),
      ...(noTeamUsers.length
        ? [
            callApi(
              "fetchLeaderboardData",
              orgId,
              { ...payload, userId: { $in: noTeamUsers.map(({ id }) => id) } },
              metricName
            ),
          ]
        : []),
    ]);

    // we could send a request per each team individually, but that's just too much requests for
    // big orgs with dozens of teams. So instead we fetch data for all teams at once and then
    // convert global rank and rankDiff to the ones local to each team.
    const ranksWithinTeam = getLocalTeamRankInformation(
      teamsWithUsers.length ? responses[2].data : [],
      teamsWithUsers
    );

    // build performance data for users and teams
    const performanceData: PerformanceData = {};
    teamsWithUsers.forEach((team) => {
      const userIds = new Set(team.users.map(({ id }) => id));
      const data = responses[2].data
        .filter(({ user }) => userIds.has(user?.id ?? 0))
        .map((item) => ({
          ...item,
          ...(ranksWithinTeam.get(team.id)?.get(item.user?.id ?? 0) ?? {}),
        }));

      upsertPerformanceData(performanceData, metricName, data, team.users, team.id);
    });
    if (owners.length) {
      const data = responses.at(noTeamUsers.length ? -2 : -1)!.data;
      upsertPerformanceData(performanceData, metricName, data, owners);
    }
    if (noTeamUsers.length) {
      upsertPerformanceData(
        performanceData,
        metricName,
        responses.at(-1)!.data,
        noTeamUsers,
        "noTeam"
      );
    }

    if (metricName in responses[0].data[0].values) {
      performanceData.undefined_undefined = {
        value: responses[0].data[0].values[metricName],
      };
    }

    responses[1].data.forEach((item) => {
      if (metricName in item.values) {
        performanceData[`${item.teamId}_undefined`] = {
          rank: item.rank,
          rankDiff: item.rankDiff,
          value: item.values[metricName],
        };
      }
    });

    yield put(fetchMetrics.success(performanceData));
  } catch (error) {
    yield handleError({ error });
    yield put(fetchMetrics.failure());
  }
}

function* onFetchDrillDownData() {
  try {
    const stackRankViewState: StackRankViewState = yield select(getStackRankViewState);
    yield call(dashboardSettings.setStackRankViewState, stackRankViewState);
    const { dateRange, filters, rankingMetric } = stackRankViewState;
    const { grouping, teamId }: StackRankDrillDownViewState = yield select(getDrillDownViewState);

    const teams: Team[] = yield select(getTeams);
    const teamUsersIds = teamId
      ? teams.find(({ id }) => id === teamId)?.users.map(({ id }) => id)
      : undefined;

    const metricName = rankingMetric.name;
    const entityType = getRankingMetricEntityType(rankingMetric);
    const fieldModel = getFieldModelByEntityType(entityType);

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: ListResponse<LeaderboardItem> = yield callApi(
      "fetchLeaderboardData",
      orgId,
      {
        ...convertToPlatformFilterModel(filters, [], fieldModel),
        date: { $gte: dateRange.startDate.toISOString(), $lte: dateRange.endDate.toISOString() },
        includePerformanceMetric: true,
        // fetch all props, including custom ones
        property: {
          $in: leaderboardFieldModel.fields.map(({ name }) => name),
        },
        summary: grouping === "team" ? "team" : "user",
        userId: grouping === "user" && teamUsersIds ? { $in: teamUsersIds } : undefined,
      },
      metricName
    );

    yield put(fetchDrillDownData.success(response.data));
  } catch (error) {
    yield handleError({ error });
    yield put(fetchDrillDownData.failure());
  }
}

export function* onExportDrillDownData() {
  const data: LeaderboardItem[] = yield select(getDrillDownData);
  const viewState: StackRankDrillDownViewState = yield select(getDrillDownViewState);
  downloadStackRankDataAsCsv(
    data,
    viewState.columns.filter(({ visible }) => visible).map(({ field }) => field)
  );
}

const DEFAULT_COLUMNS_BY_METRIC: Record<LeaderboardMetricFieldName, string[]> = {
  [LeaderboardMetricFieldName.ACTIVITIES_COMPLETED]: [
    ActivityFieldName.PRIMARY_ASSOCIATION,
    ActivityFieldName.ACTIVITY_TYPE,
    ActivityFieldName.ASSIGNEE,
    ActivityFieldName.START_AT,
    ActivityFieldName.COMPLETED_AT,
    ActivityFieldName.NOTE,
  ],
  [LeaderboardMetricFieldName.CHECK_INS_COMPLETED]: [
    ActivityFieldName.PRIMARY_ASSOCIATION,
    ActivityFieldName.ACTIVITY_TYPE,
    ActivityFieldName.ASSIGNEE,
    ActivityFieldName.COMPLETED_AT,
    ActivityFieldName.CHECK_IN_DISTANCE,
    ActivityFieldName.RELIABILITY,
    ActivityFieldName.NOTE,
  ],
  [LeaderboardMetricFieldName.COMPANIES_CREATED]: [
    CompanyFieldName.NAME,
    CompanyFieldName.USER,
    CompanyFieldName.ADDRESS,
    CompanyFieldName.CREATED_AT,
    CompanyFieldName.SOURCE,
  ],
  [LeaderboardMetricFieldName.DEALS_CLOSED_LOST]: [
    DealFieldName.NAME,
    DealFieldName.USER,
    DealFieldName.ADDRESS,
    DealFieldName.CREATED_AT,
    DealFieldName.SOURCE,
  ],
  [LeaderboardMetricFieldName.DEALS_CLOSED_WON]: [
    DealFieldName.NAME,
    DealFieldName.USER,
    DealFieldName.ADDRESS,
    DealFieldName.CREATED_AT,
    DealFieldName.SOURCE,
  ],
  [LeaderboardMetricFieldName.DEALS_CREATED]: [
    DealFieldName.NAME,
    DealFieldName.USER,
    DealFieldName.ADDRESS,
    DealFieldName.CREATED_AT,
    DealFieldName.SOURCE,
  ],
  [LeaderboardMetricFieldName.DEALS_ROTTING]: [
    DealFieldName.NAME,
    DealFieldName.USER,
    DealFieldName.ADDRESS,
    DealFieldName.CREATED_AT,
    DealFieldName.SOURCE,
  ],
  [LeaderboardMetricFieldName.NEW_PIPELINE_REVENUE]: [
    DealFieldName.NAME,
    DealFieldName.AMOUNT,
    DealFieldName.USER,
    DealFieldName.ADDRESS,
    DealFieldName.CREATED_AT,
    DealFieldName.SOURCE,
  ],
  [LeaderboardMetricFieldName.PEOPLE_CREATED]: [
    PersonFieldName.NAME,
    PersonFieldName.USER,
    PersonFieldName.ADDRESS,
    PersonFieldName.CREATED_AT,
    PersonFieldName.SOURCE,
  ],
  [LeaderboardMetricFieldName.REVENUE_CLOSED_LOST]: [
    DealFieldName.NAME,
    DealFieldName.AMOUNT,
    DealFieldName.USER,
    DealFieldName.ADDRESS,
    DealFieldName.CREATED_AT,
    DealFieldName.SOURCE,
  ],
  [LeaderboardMetricFieldName.REVENUE_CLOSED_WON]: [
    DealFieldName.NAME,
    DealFieldName.AMOUNT,
    DealFieldName.USER,
    DealFieldName.ADDRESS,
    DealFieldName.CREATED_AT,
    DealFieldName.SOURCE,
  ],
  [LeaderboardMetricFieldName.ROUTES_CREATED]: [
    RouteFieldName.NAME,
    RouteFieldName.COUNT,
    RouteFieldName.START_ADDRESS,
    RouteFieldName.CREATED_AT,
  ],
  [LeaderboardMetricFieldName.TOTAL_FACE_TIME]: [
    ActivityFieldName.PRIMARY_ASSOCIATION,
    ActivityFieldName.ACTIVITY_TYPE,
    ActivityFieldName.ASSIGNEE,
    ActivityFieldName.COMPLETED_AT,
    ActivityFieldName.CHECK_IN_DISTANCE,
    ActivityFieldName.RELIABILITY,
    ActivityFieldName.NOTE,
  ],
  [LeaderboardMetricFieldName.VERIFIED_CHECK_INS]: [
    ActivityFieldName.PRIMARY_ASSOCIATION,
    ActivityFieldName.ACTIVITY_TYPE,
    ActivityFieldName.ASSIGNEE,
    ActivityFieldName.COMPLETED_AT,
    ActivityFieldName.CHECK_IN_DISTANCE,
    ActivityFieldName.RELIABILITY,
    ActivityFieldName.NOTE,
  ],
  [LeaderboardMetricFieldName.VERIFIED_FACE_TIME]: [
    ActivityFieldName.PRIMARY_ASSOCIATION,
    ActivityFieldName.ACTIVITY_TYPE,
    ActivityFieldName.ASSIGNEE,
    ActivityFieldName.COMPLETED_AT,
    ActivityFieldName.CHECK_IN_DISTANCE,
    ActivityFieldName.RELIABILITY,
    ActivityFieldName.NOTE,
  ],
};

const DEFAULT_COLUMNS_BY_ENTITY_TYPE: Record<EntityTypesSupportingCustomMetrics, string[]> = {
  [EntityType.ACTIVITY]: [
    ActivityFieldName.PRIMARY_ASSOCIATION,
    ActivityFieldName.ACTIVITY_TYPE,
    ActivityFieldName.ASSIGNEE,
    ActivityFieldName.START_AT,
    ActivityFieldName.COMPLETED_AT,
    ActivityFieldName.CHECK_IN_DISTANCE,
    ActivityFieldName.RELIABILITY,
    ActivityFieldName.NOTE,
  ],
  [EntityType.COMPANY]: [
    CompanyFieldName.NAME,
    CompanyFieldName.USER,
    CompanyFieldName.ADDRESS,
    CompanyFieldName.CREATED_AT,
    CompanyFieldName.SOURCE,
  ],
  [EntityType.DEAL]: [
    DealFieldName.NAME,
    DealFieldName.AMOUNT,
    DealFieldName.USER,
    DealFieldName.ADDRESS,
    DealFieldName.CREATED_AT,
    DealFieldName.SOURCE,
  ],
  [EntityType.PERSON]: [
    PersonFieldName.NAME,
    PersonFieldName.USER,
    PersonFieldName.ADDRESS,
    PersonFieldName.CREATED_AT,
    PersonFieldName.SOURCE,
  ],
};

const DEFAULT_SORT_COLUMN_BY_METRIC: Record<LeaderboardMetricFieldName, string> = {
  [LeaderboardMetricFieldName.ACTIVITIES_COMPLETED]: ActivityFieldName.UPDATED_AT,
  [LeaderboardMetricFieldName.CHECK_INS_COMPLETED]: ActivityFieldName.UPDATED_AT,
  [LeaderboardMetricFieldName.COMPANIES_CREATED]: CompanyFieldName.CREATED_AT,
  [LeaderboardMetricFieldName.DEALS_CLOSED_LOST]: DealFieldName.UPDATED_AT,
  [LeaderboardMetricFieldName.DEALS_CLOSED_WON]: DealFieldName.UPDATED_AT,
  [LeaderboardMetricFieldName.DEALS_CREATED]: DealFieldName.CREATED_AT,
  [LeaderboardMetricFieldName.DEALS_ROTTING]: DealFieldName.CREATED_AT,
  [LeaderboardMetricFieldName.NEW_PIPELINE_REVENUE]: DealFieldName.UPDATED_AT,
  [LeaderboardMetricFieldName.PEOPLE_CREATED]: PersonFieldName.CREATED_AT,
  [LeaderboardMetricFieldName.REVENUE_CLOSED_LOST]: DealFieldName.UPDATED_AT,
  [LeaderboardMetricFieldName.REVENUE_CLOSED_WON]: DealFieldName.UPDATED_AT,
  [LeaderboardMetricFieldName.ROUTES_CREATED]: RouteFieldName.CREATED_AT,
  [LeaderboardMetricFieldName.TOTAL_FACE_TIME]: ActivityFieldName.UPDATED_AT,
  [LeaderboardMetricFieldName.VERIFIED_CHECK_INS]: ActivityFieldName.UPDATED_AT,
  [LeaderboardMetricFieldName.VERIFIED_FACE_TIME]: ActivityFieldName.UPDATED_AT,
};

const DEFAULT_SORT_COLUMN_BY_ENTITY_TYPE: Record<EntityTypesSupportingCustomMetrics, string> = {
  [EntityType.ACTIVITY]: ActivityFieldName.CREATED_AT,
  [EntityType.COMPANY]: CompanyFieldName.CREATED_AT,
  [EntityType.DEAL]: DealFieldName.CREATED_AT,
  [EntityType.PERSON]: PersonFieldName.CREATED_AT,
};

const DEFAULT_SORT_ORDER_BY_METRIC: Record<LeaderboardMetricFieldName, SortOrder> = {
  [LeaderboardMetricFieldName.ACTIVITIES_COMPLETED]: SortOrder.DESC,
  [LeaderboardMetricFieldName.CHECK_INS_COMPLETED]: SortOrder.DESC,
  [LeaderboardMetricFieldName.COMPANIES_CREATED]: SortOrder.DESC,
  [LeaderboardMetricFieldName.DEALS_CLOSED_LOST]: SortOrder.DESC,
  [LeaderboardMetricFieldName.DEALS_CLOSED_WON]: SortOrder.DESC,
  [LeaderboardMetricFieldName.DEALS_CREATED]: SortOrder.DESC,
  [LeaderboardMetricFieldName.DEALS_ROTTING]: SortOrder.ASC,
  [LeaderboardMetricFieldName.NEW_PIPELINE_REVENUE]: SortOrder.DESC,
  [LeaderboardMetricFieldName.PEOPLE_CREATED]: SortOrder.DESC,
  [LeaderboardMetricFieldName.REVENUE_CLOSED_LOST]: SortOrder.DESC,
  [LeaderboardMetricFieldName.REVENUE_CLOSED_WON]: SortOrder.DESC,
  [LeaderboardMetricFieldName.ROUTES_CREATED]: SortOrder.DESC,
  [LeaderboardMetricFieldName.TOTAL_FACE_TIME]: SortOrder.DESC,
  [LeaderboardMetricFieldName.VERIFIED_CHECK_INS]: SortOrder.DESC,
  [LeaderboardMetricFieldName.VERIFIED_FACE_TIME]: SortOrder.DESC,
};

const DEFAULT_SORT_ORDER_BY_ENTITY_TYPE: Record<EntityTypesSupportingCustomMetrics, SortOrder> = {
  [EntityType.ACTIVITY]: SortOrder.DESC,
  [EntityType.COMPANY]: SortOrder.DESC,
  [EntityType.DEAL]: SortOrder.DESC,
  [EntityType.PERSON]: SortOrder.DESC,
};

const USER_DRILL_DOWN_API_METHOD_BY_METRIC: Record<LeaderboardMetricFieldName, ApiMethodName> = {
  [LeaderboardMetricFieldName.ACTIVITIES_COMPLETED]: "fetchActivities",
  [LeaderboardMetricFieldName.CHECK_INS_COMPLETED]: "fetchActivities",
  [LeaderboardMetricFieldName.COMPANIES_CREATED]: "fetchCompanies",
  [LeaderboardMetricFieldName.DEALS_CLOSED_LOST]: "fetchDeals",
  [LeaderboardMetricFieldName.DEALS_CLOSED_WON]: "fetchDeals",
  [LeaderboardMetricFieldName.DEALS_CREATED]: "fetchDeals",
  [LeaderboardMetricFieldName.DEALS_ROTTING]: "fetchDeals",
  [LeaderboardMetricFieldName.NEW_PIPELINE_REVENUE]: "fetchDeals",
  [LeaderboardMetricFieldName.PEOPLE_CREATED]: "fetchPeople",
  [LeaderboardMetricFieldName.REVENUE_CLOSED_LOST]: "fetchDeals",
  [LeaderboardMetricFieldName.REVENUE_CLOSED_WON]: "fetchDeals",
  [LeaderboardMetricFieldName.ROUTES_CREATED]: "fetchUnifiedRoutes",
  [LeaderboardMetricFieldName.TOTAL_FACE_TIME]: "fetchActivities",
  [LeaderboardMetricFieldName.VERIFIED_CHECK_INS]: "fetchActivities",
  [LeaderboardMetricFieldName.VERIFIED_FACE_TIME]: "fetchActivities",
};

const USER_DRILL_DOWN_API_METHOD_BY_ENTITY_TYPE: Record<
  EntityTypesSupportingCustomMetrics,
  ApiMethodName
> = {
  [EntityType.ACTIVITY]: "fetchActivities",
  [EntityType.COMPANY]: "fetchCompanies",
  [EntityType.DEAL]: "fetchDeals",
  [EntityType.PERSON]: "fetchPeople",
};

export function* onShowUserDrillDown({ payload }: ReturnType<typeof showUserDrillDown.request>) {
  const userIds = payload;
  const { dateRange, filters, rankingMetric }: StackRankViewState =
    yield select(getStackRankViewState);

  const metricName = rankingMetric.name;
  const entityType = getRankingMetricEntityType(rankingMetric);
  const fieldModel = getFieldModelByEntityType(entityType);

  const drillDownViewState: DrillDownViewState = yield select(getDrillDownViewState);
  const viewState = localSettings.getViewSettings(`dashboard/stackRank/${metricName}`, fieldModel, {
    ...drillDownViewState,
    columns: getDefaultDrillDownColumns(
      isCustomField(rankingMetric)
        ? DEFAULT_COLUMNS_BY_ENTITY_TYPE[entityType as EntityTypesSupportingCustomMetrics]
        : DEFAULT_COLUMNS_BY_METRIC[metricName as LeaderboardMetricFieldName],
      fieldModel
    ),
    range: { endRow: DEFAULT_PAGE_SIZE, startRow: 0 },
    sort: [
      {
        field: fieldModel.getByName(
          isCustomField(rankingMetric)
            ? DEFAULT_SORT_COLUMN_BY_ENTITY_TYPE[entityType as EntityTypesSupportingCustomMetrics]
            : DEFAULT_SORT_COLUMN_BY_METRIC[metricName as LeaderboardMetricFieldName]
        )!,
        order: isCustomField(rankingMetric)
          ? DEFAULT_SORT_ORDER_BY_ENTITY_TYPE[entityType as EntityTypesSupportingCustomMetrics]
          : DEFAULT_SORT_ORDER_BY_METRIC[metricName as LeaderboardMetricFieldName],
      },
    ],
  });

  let filter: FilterModel =
    metricName !== LeaderboardMetricFieldName.ROUTES_CREATED ? filters : createEmptyFilterModel();
  if (userIds && userIds.length) {
    filter = upsertSimpleConditions(
      filter,
      createSimpleCondition(CommonFieldName.USER, FilterOperator.IN_ANY, userIds)
    );
  }

  switch (metricName) {
    case LeaderboardMetricFieldName.ACTIVITIES_COMPLETED:
      filter = upsertSimpleConditions(
        filter,
        createSimpleCondition(ActivityFieldName.COMPLETED, FilterOperator.EQUALS, true),
        createSimpleCondition(ActivityFieldName.COMPLETED_AT, FilterOperator.IN_RANGE, [
          dateRange.startDate,
          dateRange.endDate,
        ])
      );
      break;
    case LeaderboardMetricFieldName.CHECK_INS_COMPLETED:
      filter = upsertSimpleConditions(
        filter,
        createSimpleCondition(ActivityFieldName.COMPLETED, FilterOperator.EQUALS, true),
        createSimpleCondition(ActivityFieldName.COMPLETED_AT, FilterOperator.IN_RANGE, [
          dateRange.startDate,
          dateRange.endDate,
        ]),
        createSimpleCondition(ActivityFieldName.RELIABILITY, FilterOperator.NOT_EMPTY)
      );
      break;
    case LeaderboardMetricFieldName.COMPANIES_CREATED:
      filter = upsertSimpleConditions(
        filter,
        createSimpleCondition(CompanyFieldName.CREATED_AT, FilterOperator.IN_RANGE, [
          dateRange.startDate,
          dateRange.endDate,
        ])
      );
      break;
    case LeaderboardMetricFieldName.DEALS_CLOSED_LOST:
      filter = upsertSimpleConditions(
        filter,
        createSimpleCondition(DealFieldName.CLOSING_DATE, FilterOperator.IN_RANGE, [
          dateRange.startDate,
          dateRange.endDate,
        ]),
        createSimpleCondition(DealFieldName.STAGE_TYPE, FilterOperator.IN_ANY, [DealStageType.LOST])
      );
      break;
    case LeaderboardMetricFieldName.DEALS_CLOSED_WON:
      filter = upsertSimpleConditions(
        filter,
        createSimpleCondition(DealFieldName.CLOSING_DATE, FilterOperator.IN_RANGE, [
          dateRange.startDate,
          dateRange.endDate,
        ]),
        createSimpleCondition(DealFieldName.STAGE_TYPE, FilterOperator.IN_ANY, [DealStageType.WON])
      );
      break;
    case LeaderboardMetricFieldName.DEALS_CREATED:
      filter = upsertSimpleConditions(
        filter,
        createSimpleCondition(DealFieldName.CREATED_AT, FilterOperator.IN_RANGE, [
          dateRange.startDate,
          dateRange.endDate,
        ])
      );
      break;
    case LeaderboardMetricFieldName.DEALS_ROTTING:
      filter = upsertSimpleConditions(
        filter,
        createSimpleCondition(DealFieldName.ROTTING_DAYS_OUT, FilterOperator.IS_OVERDUE)
      );
      break;
    case LeaderboardMetricFieldName.NEW_PIPELINE_REVENUE:
      filter = upsertSimpleConditions(
        filter,
        createSimpleCondition(DealFieldName.CREATED_AT, FilterOperator.IN_RANGE, [
          dateRange.startDate,
          dateRange.endDate,
        ])
      );
      break;
    case LeaderboardMetricFieldName.PEOPLE_CREATED:
      filter = upsertSimpleConditions(
        filter,
        createSimpleCondition(PersonFieldName.CREATED_AT, FilterOperator.IN_RANGE, [
          dateRange.startDate,
          dateRange.endDate,
        ])
      );
      break;
    case LeaderboardMetricFieldName.REVENUE_CLOSED_LOST:
      filter = upsertSimpleConditions(
        filter,
        createSimpleCondition(DealFieldName.CLOSING_DATE, FilterOperator.IN_RANGE, [
          dateRange.startDate,
          dateRange.endDate,
        ]),
        createSimpleCondition(DealFieldName.STAGE_TYPE, FilterOperator.IN_ANY, [DealStageType.LOST])
      );
      break;
    case LeaderboardMetricFieldName.REVENUE_CLOSED_WON:
      filter = upsertSimpleConditions(
        filter,
        createSimpleCondition(DealFieldName.CLOSING_DATE, FilterOperator.IN_RANGE, [
          dateRange.startDate,
          dateRange.endDate,
        ]),
        createSimpleCondition(DealFieldName.STAGE_TYPE, FilterOperator.IN_ANY, [DealStageType.WON])
      );
      break;
    case LeaderboardMetricFieldName.ROUTES_CREATED:
      filter = upsertSimpleConditions(
        filter,
        createSimpleCondition(RouteFieldName.CREATED_AT, FilterOperator.IN_RANGE, [
          dateRange.startDate,
          dateRange.endDate,
        ])
      );
      break;
    case LeaderboardMetricFieldName.VERIFIED_CHECK_INS:
      filter = upsertSimpleConditions(
        filter,
        createSimpleCondition(ActivityFieldName.COMPLETED, FilterOperator.EQUALS, true),
        createSimpleCondition(ActivityFieldName.COMPLETED_AT, FilterOperator.IN_RANGE, [
          dateRange.startDate,
          dateRange.endDate,
        ]),
        createSimpleCondition(ActivityFieldName.RELIABILITY, FilterOperator.EQUALS, true)
      );
      break;
  }

  viewState.filter = filter;

  yield put(showUserDrillDown.success({ viewState }));
}

export function* onFetchUserDrillDownData({
  payload,
}: ReturnType<typeof fetchUserDrillDownData.request>) {
  try {
    loggingService.debug("Dashboard: stack rank, onFetchUserDrillDownData", payload);

    const { rankingMetric }: StackRankViewState = yield select(getStackRankViewState);
    const metricName = rankingMetric.name;
    const entityType = getRankingMetricEntityType(rankingMetric);
    const fieldModel = getFieldModelByEntityType(entityType);

    if (!payload.updateOnly) {
      // We do not listen to filter returned by AgGrid from PlatformDataSource
      delete payload.request.filter;
    }

    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applyUserDrillDownListViewSettings(payload.request));
    }

    const drillDownViewState: DrillDownViewState = yield select(getUserDrillDownViewState);

    if (!payload.fetchOnlyWithoutFilters) {
      localSettings.setViewSettings(drillDownViewState, `dashboard/stackRank/${metricName}`);
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.startRow
        : drillDownViewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.endRow - payload.request.range.startRow
        : drillDownViewState.range.endRow - drillDownViewState.range.startRow;

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const requestPayload = {
      $filters: {
        includeAccessStatus: true,
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : drillDownViewState.filter,
          drillDownViewState.columns,
          fieldModel
        ),
      },
      $limit,
      $offset,
      $order: convertToPlatformSortModel(drillDownViewState.sort),
    };

    const apiMethod = isCustomField(rankingMetric)
      ? USER_DRILL_DOWN_API_METHOD_BY_ENTITY_TYPE[entityType as EntityTypesSupportingCustomMetrics]
      : USER_DRILL_DOWN_API_METHOD_BY_METRIC[metricName as LeaderboardMetricFieldName];
    const response: ListResponse<AnyEntity> = yield callApi(apiMethod, orgId, requestPayload);

    if (payload.dataCallback) {
      yield call(payload.dataCallback, response);
    }

    yield put(
      fetchUserDrillDownData.success({
        totalFilteredRecords: response.total,
        totalRecords: response.accessible,
      })
    );
  } catch (error) {
    payload.failCallback?.();
    yield put(fetchUserDrillDownData.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateStackRankViewState() {
  const stackRankViewState: StackRankViewState = yield select(getStackRankViewState);
  yield call(dashboardSettings.setStackRankViewState, stackRankViewState);
}

export function* onUpdateDrillDownViewState() {
  const drillDownViewState: StackRankDrillDownViewState = yield select(getDrillDownViewState);
  yield call(dashboardSettings.setStackRankDrillDownViewState, drillDownViewState);
}

export function* stackRankSagas() {
  yield takeLatest(initializeStackRank.request, onInitialize);
  yield takeLatest(fetchMetrics.request, onFetchMetrics);
  yield takeLatest(fetchDrillDownData.request, onFetchDrillDownData);
  yield takeLatest(exportDrillDownData, onExportDrillDownData);
  yield takeLatest(showUserDrillDown.request, onShowUserDrillDown);
  yield takeLatest(fetchUserDrillDownData.request, onFetchUserDrillDownData);
  yield takeLatest(updateStackRankViewState, onUpdateStackRankViewState);
  yield takeLatest(updateDrillDownViewState, onUpdateDrillDownViewState);
}
